import {
	type UpdateTeamInput,
	UpdateTeamSchema,
	type UpdateTeamSettingsInput,
	UpdateTeamSettingsSchema,
} from "@gosaas/schemas";
import { hasPermission, type Permission, type Role } from "@gosaas/types/rbac";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconSettings } from "@tabler/icons-react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useTeam } from "@/components/providers/team";
import { useFetchTeamSettings } from "@/components/queries/team";
import { DeveloperAccessCard } from "@/components/team/developer-access-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	EmojiPicker,
	EmojiPickerContent,
	EmojiPickerSearch,
} from "@/components/ui/emoji-picker";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { logDevError } from "@/lib/dev-log";
import { isPublicApiSurfaceEnabled } from "@/lib/modules";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

export const Route = createFileRoute("/_authorized/team/settings/")({
	component: Settings,
});

export function decodeTeamEmoji(emoji?: string): string {
	return emoji ? decodeURIComponent(emoji) : "🔍";
}

export function buildTeamProfileFormDefaults(team?: {
	name?: string;
	emoji?: string;
	url?: string;
	email?: string;
}) {
	return {
		name: team?.name || "",
		emoji: decodeTeamEmoji(team?.emoji),
		url: team?.url || "",
		email: team?.email || "",
	};
}

export function buildTeamSettingsFormDefaults(
	teamSettings?: { sync_users?: boolean } | null,
) {
	return {
		sync_users: teamSettings?.sync_users || false,
	};
}

export function shouldShowDeveloperAccessCard(
	publicApiEnabled: boolean,
	canReadSecurity: boolean,
): boolean {
	return publicApiEnabled && canReadSecurity;
}

export function isSyncUsersControlDisabled(
	disabled: boolean,
	teamType?: string,
): boolean {
	return disabled || teamType !== "parent";
}

export function teamEmojiPreview(
	fieldValue?: string,
	teamEmoji?: string,
): string {
	return fieldValue || teamEmoji || "🔍";
}

export function Settings() {
	const { teamID, teams, refresh } = useTeam();
	const router = useRouter();
	const team = teams.find((t) => t.id === teamID);

	const canReadTeam = hasPermission(
		team?.role as Role,
		"account:read" as Permission,
	);

	const { teamSettings, isLoadingSettings, refetchTeamSettings } =
		useFetchTeamSettings(teamID as string);

	const form = useForm({
		resolver: zodResolver(UpdateTeamSchema),
		defaultValues: buildTeamProfileFormDefaults(team),
	});

	const formSettings = useForm({
		resolver: zodResolver(UpdateTeamSettingsSchema),
		defaultValues: buildTeamSettingsFormDefaults(teamSettings),
	});

	useEffect(() => {
		form.reset(buildTeamProfileFormDefaults(team));
	}, [team, form]);

	useEffect(() => {
		if (!canReadTeam) {
			toast.warning("You do not have permission to read team settings", {
				description: "Please contact support if you believe this is an error.",
			});
			router.navigate({ to: "/403" });
		}
	}, [canReadTeam, router]);

	if (!canReadTeam) {
		return null;
	}

	const disabled = !hasPermission(
		team?.role as Role,
		"account:update" as Permission,
	);

	const onSubmit = async (data: UpdateTeamInput) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.teamUpdate(teamID as string, {
					...data,
					emoji: encodeURIComponent(data.emoji),
				}),
			);
			await refresh();
			toast.success("Team settings updated successfully");
		} catch (error) {
			logDevError(
				"Failed to update team:",
				error instanceof TypedClientApiError
					? error.message
					: "An unknown error occurred",
			);
			toast.error("Failed to update team settings", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred",
			});
		}
	};

	const updateTeamSettings = async (data: UpdateTeamSettingsInput) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.teamSettingsUpdate(teamID as string, {
					sync_users: data.sync_users,
				}),
			);
			await refetchTeamSettings();
			toast.success("Team settings updated successfully");
		} catch (error) {
			logDevError(
				"Failed to update team settings:",
				error instanceof TypedClientApiError
					? error.message
					: "An unknown error occurred",
			);
			toast.error("Failed to update team settings", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred",
			});
		}
	};

	const canReadSecurity = hasPermission(
		team?.role as Role,
		"security:read" as Permission,
	);
	const publicApiEnabled = isPublicApiSurfaceEnabled();
	const showDeveloperAccess = shouldShowDeveloperAccessCard(
		publicApiEnabled,
		canReadSecurity,
	);

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Team", href: null },
					{ title: "Settings", href: "/team/settings" },
				]}
				title="Team Settings"
				description="Manage your team profile and account configuration"
				icon={<IconSettings className="size-6 text-primary" aria-hidden />}
			/>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-blue-500/10 rounded-lg">
							<IconSettings className="h-5 w-5 text-blue-500" />
						</div>
						<div>
							<CardTitle className="text-xl">Settings</CardTitle>
							<p className="text-sm text-muted-foreground mt-1">
								Manage your team profile and account configuration
							</p>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
							<FormField
								control={form.control}
								name="emoji"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-base font-medium">
											Team Emoji
										</FormLabel>
										<div className="flex items-center gap-6">
											<div className="relative">
												<div className="size-20 bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl flex items-center justify-center border-2 border-primary/20 shadow-sm">
													<span className="text-4xl">
														{teamEmojiPreview(field.value, team?.emoji)}
													</span>
												</div>
											</div>
											<div className="flex flex-col gap-2">
												<Popover>
													<PopoverTrigger
														className="focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-input bg-background text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 cursor-pointer hover:bg-primary/5 transition-colors bg-transparent"
														disabled={disabled}
													>
														Choose Emoji
													</PopoverTrigger>
													<PopoverContent className="w-full p-0 h-72">
														<EmojiPicker
															onEmojiSelect={(emoji) =>
																field.onChange(emoji.emoji)
															}
														>
															<EmojiPickerSearch />
															<EmojiPickerContent />
														</EmojiPicker>
													</PopoverContent>
												</Popover>
												<p className="text-xs text-muted-foreground">
													Choose an emoji to represent your team
												</p>
											</div>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-base font-medium">
											Team name
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												value={field.value || team?.name || ""}
												disabled={disabled}
												className="h-11 bg-background/50 border-border/50 focus:border-primary/50 transition-colors"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<FormField
									control={form.control}
									name="url"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-base font-medium">
												Website URL
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder={team?.url || "https://"}
													value={field.value || team?.url || ""}
													disabled={disabled}
													className="h-11 bg-background/50 border-border/50 focus:border-primary/50 transition-colors"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-base font-medium">
												Contact email
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="email"
													placeholder={team?.email || "team@example.com"}
													value={field.value || team?.email || ""}
													disabled={disabled}
													className="h-11 bg-background/50 border-border/50 focus:border-primary/50 transition-colors"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="pt-2">
								<Button
									type="submit"
									className="h-11 px-8 bg-primary hover:bg-primary/90 transition-colors"
									disabled={disabled}
								>
									Save changes
								</Button>
							</div>
						</form>
					</Form>
				</CardContent>
			</Card>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-purple-500/10 rounded-lg">
							<IconSettings className="h-5 w-5 text-purple-500" />
						</div>
						<div>
							<CardTitle className="text-xl">Configuration</CardTitle>
							<p className="text-sm text-muted-foreground mt-1">
								Configure account settings and behaviors
							</p>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					{isLoadingSettings ? (
						<div className="flex items-center justify-center py-6">
							<div className="h-8 w-8 animate-spin rounded-full border-b-2 border-foreground" />
						</div>
					) : teamSettings ? (
						<Form {...formSettings}>
							<form
								onSubmit={formSettings.handleSubmit(updateTeamSettings)}
								className="w-full space-y-6"
							>
								<div className="space-y-4">
									<FormField
										control={formSettings.control}
										name="sync_users"
										render={({ field }) => (
											<FormItem className="flex flex-row items-center justify-between rounded-lg border border-border p-4 shadow-sm transition-colors hover:border-muted-foreground/25">
												<div className="space-y-1.5">
													<FormLabel className="text-base">
														Keep users in sync (Parent only)
													</FormLabel>
													<FormDescription className="text-sm text-muted-foreground">
														Automatically sync users from parent account to all
														your children accounts.
													</FormDescription>
												</div>
												<FormControl>
													<Switch
														checked={field.value}
														onCheckedChange={field.onChange}
														disabled={isSyncUsersControlDisabled(
															disabled,
															team?.type,
														)}
														className="data-[state=checked]:bg-blue-600"
													/>
												</FormControl>
											</FormItem>
										)}
									/>
								</div>
								<Button
									type="submit"
									className="h-11 px-8 bg-primary hover:bg-primary/90 transition-colors w-full sm:w-auto"
									disabled={isSyncUsersControlDisabled(disabled, team?.type)}
								>
									Save Settings
								</Button>
							</form>
						</Form>
					) : (
						<div className="py-6 text-center text-muted-foreground">
							No settings available for this team.
						</div>
					)}
				</CardContent>
			</Card>

			{showDeveloperAccess && <DeveloperAccessCard />}
		</PageLayout>
	);
}
